import { useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { rankSearch, type SearchItem } from './useSearchIndex.js';

export type CommandPaletteProps = {
  open: boolean;
  items: readonly SearchItem[];
  onClose: () => void;
  onNavigate: (href: string) => void;
};

const KIND_LABEL = { group: 'Group', url: 'URL' } as const;

export function CommandPalette({ open, items, onClose, onNavigate }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useMemo(() => rankSearch(items, query).slice(0, 12), [items, query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      inputRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  if (!open) return null;

  const activate = (index: number) => {
    const item = results[index];
    if (item !== undefined) {
      onNavigate(item.href);
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-(--color-scrim) p-(--space-6)"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search groups and URLs"
        className="w-full max-w-[36rem] overflow-hidden rounded-(--radius-lg) border border-rule bg-surface-raised shadow-(--shadow-overlay)"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            onClose();
          } else if (event.key === 'Tab') {
            // The input is the only focusable element in here, so holding Tab
            // on it is the whole focus trap: a modal must not leak focus to the
            // page behind it.
            event.preventDefault();
            inputRef.current?.focus();
          } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive((current) => Math.min(current + 1, results.length - 1));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((current) => Math.max(current - 1, 0));
          } else if (event.key === 'Enter') {
            event.preventDefault();
            activate(active);
          }
        }}
      >
        <input
          ref={inputRef}
          role="combobox"
          aria-expanded={results.length > 0}
          aria-controls={results.length === 0 ? undefined : 'palette-results'}
          aria-activedescendant={results[active] === undefined ? undefined : `palette-${active}`}
          aria-label="Search"
          placeholder="Search groups and URLs…"
          /* The ring is replaced rather than dropped: this field sits inside a
             panel that clips its own corners, so an outlined ring showed only
             as a stray accent line across the edge. The rule under the field
             turning accent is the same signal, drawn where it fits. */
          className="w-full border-b border-rule bg-transparent px-(--space-4) py-(--space-3) text-[length:var(--text-lead)] text-ink placeholder:text-ink-subtle focus-visible:border-b-accent focus-visible:outline-none"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />

        {results.length === 0 ? (
          <p role="status" className="px-(--space-4) py-(--space-5) text-ink-subtle">
            Nothing matched “{query}”.
          </p>
        ) : (
          <ul id="palette-results" role="listbox" className="max-h-[24rem] overflow-y-auto">
            {results.map((item, index) => (
              <li
                key={`${item.kind}-${item.id}`}
                id={`palette-${index}`}
                role="option"
                aria-selected={index === active}
                onMouseEnter={() => setActive(index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  activate(index);
                }}
                className={clsx(
                  'flex cursor-pointer items-baseline justify-between gap-(--space-4) border-b border-rule px-(--space-4) py-(--space-3) last:border-b-0',
                  index === active && 'bg-surface-hover',
                )}
              >
                <span
                  className={clsx(
                    'truncate',
                    item.kind === 'url' ? 'font-mono text-[length:var(--text-small)]' : '',
                  )}
                >
                  {item.label}
                </span>
                <span className="flex shrink-0 items-baseline gap-(--space-3) text-[length:var(--text-small)] text-ink-subtle">
                  <span className="truncate">{item.sublabel}</span>
                  <span className="text-[length:var(--text-micro)] uppercase tracking-wide">
                    {KIND_LABEL[item.kind]}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
